import { Fragment, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from 'components/ui/card';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import { useUsageAgentNames, useUsageUserNames } from 'features/usage/hooks/use-usage';
import {
  TokenUsageAgentEntry,
  TokenUsageModelEntry,
  TokenUsageUserEntry,
} from 'features/usage/services/usage-service';
import {
  DEFAULT_CHAT_AGENT_ID,
  formatNumber,
  formatPercent,
  getAverage,
  getRatio,
  getUserLabel,
} from 'features/usage/utils/usage-format';
import { cn } from 'lib/utils';
import { UsageCardHeader } from './usage-card-header';

type UsageTab = 'models' | 'agents' | 'people';

type BreakdownRow = {
  key: string;
  name: string;
  subName?: string;
  isMuted: boolean;
  count: number;
  input: number;
  output: number;
  total: number;
  details: Array<{ label: string; value: string }>;
};

const headCell = 'h-10 px-2 text-right text-xs font-medium text-muted-foreground md:px-3';
const bodyCell = 'px-2 py-4 text-right text-sm text-muted-foreground md:px-3';
// Phones show name, total and share; turns, input and output move into the expanded row.
const desktopOnlyCell = 'hidden md:table-cell';

export function UsageWhoUsedCard({
  models,
  agents,
  users,
  showUsers,
  isOwnUsage = false,
  totalTokens,
  compact = false,
}: {
  models: TokenUsageModelEntry[];
  agents: TokenUsageAgentEntry[];
  users: TokenUsageUserEntry[];
  showUsers: boolean;
  isOwnUsage?: boolean;
  totalTokens: number;
  /** Narrow layout for a side panel: only name, total tokens and share, as in Figma. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<UsageTab | null>(null);
  const [openRowKey, setOpenRowKey] = useState<string | null>(null);

  const agentIds = useMemo(
    () => agents.map((agent) => agent.agent_id).filter((agentId): agentId is string => !!agentId),
    [agents]
  );
  const userIds = useMemo(
    () =>
      showUsers
        ? users.map((user) => user.user_id).filter((userId): userId is string => !!userId)
        : [],
    [users, showUsers]
  );
  const agentNames = useUsageAgentNames(agentIds);
  const userNames = useUsageUserNames(userIds);

  const tabs: Array<{ value: UsageTab; label: string }> = [
    { value: 'models', label: t('USAGE_TAB_MODELS', { defaultValue: 'Models' }) },
    { value: 'agents', label: t('USAGE_TAB_AGENTS', { defaultValue: 'Agents' }) },
  ];
  // Only the organization view returns a per-person breakdown; "me" and single-member views don't.
  if (showUsers) tabs.unshift({ value: 'people', label: t('USAGE_TAB_PEOPLE', { defaultValue: 'People' }) });
  // Until a tab is picked, or when People disappears on the personal view, the first tab is shown.
  const activeTab = tab && tabs.some((item) => item.value === tab) ? tab : tabs[0].value;

  const getAgentName = (agentId: string) => {
    if (agentId === DEFAULT_CHAT_AGENT_ID) return { name: t('DEFAULT_CHAT') };
    if (!agentId) return { name: t('UNKNOWN') };
    if (agentNames[agentId]) return { name: agentNames[agentId] };
    return { name: t('UNNAMED_AGENT', { defaultValue: 'Unnamed agent' }), subName: getUserLabel(agentId) };
  };

  const perTurnDetails = (entry: TokenUsageAgentEntry | TokenUsageUserEntry) => [
    { label: t('INPUT_TOKENS'), value: t('AVG_SHORT', { avg: getAverage(entry.input_tokens, entry.turn_count) }) },
    { label: t('OUTPUT_TOKENS'), value: t('AVG_SHORT', { avg: getAverage(entry.output_tokens, entry.turn_count) }) },
    { label: t('RATIO'), value: getRatio(entry.input_tokens, entry.output_tokens) },
  ];

  const getRows = (): BreakdownRow[] => {
    if (activeTab === 'agents') {
      return agents.map((agent, index) => ({
        key: agent.agent_id || `unknown-agent-${index}`,
        ...getAgentName(agent.agent_id),
        isMuted: !agent.agent_id,
        count: agent.turn_count,
        input: agent.input_tokens,
        output: agent.output_tokens,
        total: agent.total_tokens,
        details: perTurnDetails(agent),
      }));
    }

    if (activeTab === 'people') {
      return users.map((user, index) => ({
        key: user.user_id ?? `unknown-user-${index}`,
        name: user.user_id ? userNames[user.user_id] || getUserLabel(user.user_id) : t('UNKNOWN'),
        isMuted: !user.user_id,
        count: user.turn_count,
        input: user.input_tokens,
        output: user.output_tokens,
        total: user.total_tokens,
        details: perTurnDetails(user),
      }));
    }

    return models.map((model) => ({
      key: model.model,
      name: model.model,
      isMuted: model.model === 'unknown',
      count: model.model_call_count,
      input: model.input_tokens,
      output: model.output_tokens,
      total: model.total_tokens,
      details: [
        { label: t('TOTAL_TOKENS'), value: t('AVG_TOK', { avg: getAverage(model.total_tokens, model.model_call_count) }) },
        { label: t('INPUT_TOKENS'), value: t('AVG_SHORT', { avg: getAverage(model.input_tokens, model.model_call_count) }) },
        { label: t('OUTPUT_TOKENS'), value: t('AVG_SHORT', { avg: getAverage(model.output_tokens, model.model_call_count) }) },
        { label: t('RATIO'), value: getRatio(model.input_tokens, model.output_tokens) },
      ],
    }));
  };

  const rows = getRows();
  const nameHeader = {
    models: t('MODEL'),
    agents: t('AGENT'),
    people: t('PERSON', { defaultValue: 'Person' }),
  }[activeTab];
  const countHeader = activeTab === 'models' ? t('MODEL_CALLS') : t('TURNS');
  const columnCount = compact ? 4 : 7;

  const getTitle = () => {
    if (compact) return t('WHAT_USED_TOKENS', { defaultValue: 'What used the tokens' });
    if (isOwnUsage) return t('WHAT_USED_YOUR_TOKENS', { defaultValue: 'What used your tokens' });
    return t('WHO_USED_TOKENS', { defaultValue: 'Who and what used the tokens' });
  };

  const handleTabChange = (value: string) => {
    setTab(value as UsageTab);
    setOpenRowKey(null);
  };

  return (
    <Card className="rounded-section border-border/70 shadow-none">
      <CardContent className="flex flex-col gap-5 p-5 md:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <UsageCardHeader
            title={getTitle()}
            subtitle={
              compact
                ? t('HIGHEST_USE_FIRST', { defaultValue: 'Highest use first' })
                : t('WHO_USED_TOKENS_SUBTITLE', {
                    defaultValue: 'Highest use first. Open a row for more detail.',
                  })
            }
          />
          {tabs.length > 1 && (
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full sm:w-auto">
              <TabsList className="flex h-auto w-full gap-1 rounded-full border border-border bg-card p-1 sm:inline-flex sm:w-auto">
                {tabs.map((item) => (
                  <TabsTrigger
                    key={item.value}
                    value={item.value}
                    className="min-w-0 flex-1 rounded-full px-3 py-1.5 text-sm sm:flex-none sm:px-4 font-medium text-muted-foreground data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=active]:shadow-none"
                  >
                    {item.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          )}
        </div>

        {/* overflow-x-auto also turns on vertical scrolling; the collapsed table borders spill a
            fraction of a pixel below the table, which showed a second, inner scrollbar. */}
        <div className="overflow-x-auto overflow-y-hidden">
          <table className="w-full border-collapse md:min-w-max">
            <thead>
              <tr className="border-b border-border">
                <th className={cn(headCell, 'pl-0 text-left')}>{nameHeader}</th>
                {!compact && (
                  <>
                    <th className={cn(headCell, desktopOnlyCell)}>{countHeader}</th>
                    <th className={cn(headCell, desktopOnlyCell)}>{t('INPUT_TOKENS')}</th>
                    <th className={cn(headCell, desktopOnlyCell)}>{t('OUTPUT_TOKENS')}</th>
                  </>
                )}
                <th className={headCell}>{t('TOTAL_TOKENS')}</th>
                <th className={cn(headCell, 'text-left')}>{t('SHARE')}</th>
                <th className="w-10" aria-hidden />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr className="border-b border-border">
                  <td colSpan={columnCount} className="py-6 text-center text-sm text-muted-foreground">
                    {t('NO_DATA_FOR_RANGE')}
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                const share = totalTokens ? (row.total / totalTokens) * 100 : 0;
                const isOpen = openRowKey === row.key;

                return (
                  <Fragment key={row.key}>
                    <tr className={cn('border-b border-border', row.isMuted && 'opacity-60')}>
                      <td className="py-4 pr-2 md:pr-3">
                        <p className="break-words text-sm text-foreground">{row.name}</p>
                        {row.subName && (
                          <p className="break-words text-xs text-muted-foreground">{row.subName}</p>
                        )}
                      </td>
                      {!compact && (
                        <>
                          <td className={cn(bodyCell, desktopOnlyCell)}>{formatNumber(row.count)}</td>
                          <td className={cn(bodyCell, desktopOnlyCell)}>{formatNumber(row.input)}</td>
                          <td className={cn(bodyCell, desktopOnlyCell)}>{formatNumber(row.output)}</td>
                        </>
                      )}
                      <td className={cn(bodyCell, 'whitespace-nowrap font-medium text-foreground')}>
                        {formatNumber(row.total)}
                      </td>
                      <td className="px-2 py-4 md:px-3">
                        <div className="flex items-center md:gap-4">
                          {/* The bar is hidden on phones; the percentage alone fits the narrow row. */}
                          <div
                            className={cn(
                              'hidden h-1.5 overflow-hidden rounded-full bg-muted md:block',
                              compact ? 'w-16' : 'w-32'
                            )}
                          >
                            {share > 0 && (
                              <div
                                className="h-full min-w-1.5 rounded-full bg-usage-chart-1"
                                style={{ width: `${Math.min(100, share)}%` }}
                              />
                            )}
                          </div>
                          <span className="w-12 text-right text-sm text-foreground">
                            {formatPercent(share)}
                          </span>
                        </div>
                      </td>
                      <td className="py-2 pl-1 text-right">
                        <button
                          type="button"
                          aria-expanded={isOpen}
                          aria-label={t('SHOW_DETAILS_FOR', {
                            name: row.name,
                            defaultValue: 'Show details for {{name}}',
                          })}
                          onClick={() => setOpenRowKey(isOpen ? null : row.key)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
                        >
                          <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b border-border">
                        <td colSpan={columnCount} className="pb-4">
                          <dl className="grid grid-cols-2 gap-3 rounded-nav bg-muted/50 p-3 sm:grid-cols-4">
                            {!compact &&
                              [
                                { label: countHeader, value: formatNumber(row.count) },
                                { label: t('INPUT_TOKENS'), value: formatNumber(row.input) },
                                { label: t('OUTPUT_TOKENS'), value: formatNumber(row.output) },
                              ].map((detail) => (
                                <div key={`mobile-${detail.label}`} className="md:hidden">
                                  <dt className="text-xs text-muted-foreground">{detail.label}</dt>
                                  <dd className="text-sm text-foreground">{detail.value}</dd>
                                </div>
                              ))}
                            {row.details.map((detail) => (
                              <div key={detail.label}>
                                <dt className="text-xs text-muted-foreground">{detail.label}</dt>
                                <dd className="text-sm text-foreground">{detail.value}</dd>
                              </div>
                            ))}
                          </dl>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
